import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { DialogHeader } from './DialogHeader';

interface TemplateFormProps {
  templateName: string;
  description: string;
  onTemplateNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  onClose: () => void;
  isLoading: boolean;
  requiresDescription: boolean;
}

export const TemplateForm: React.FC<TemplateFormProps> = ({
  templateName,
  description,
  onTemplateNameChange,
  onDescriptionChange,
  onSubmit,
  onCancel,
  onClose,
  isLoading,
  requiresDescription,
}) => {
  const isSubmitDisabled =
    isLoading ||
    !templateName.trim() ||
    (requiresDescription ? !description.trim() : false);

  return (
    <>
      <DialogHeader
        title="Customize Template"
        subtitle="Enter a name and description for your automation template."
        onClose={onClose}
        showCloseButton
      />
      <form onSubmit={onSubmit} className="px-6 py-4">
        <div className="space-y-4">
          <div>
            <label
              htmlFor="template-name"
              className="block text-sm font-medium text-gray-700 mb-1">
              Template Name
            </label>
            <Input
              type="text"
              id="template-name"
              value={templateName}
              onChange={e => onTemplateNameChange(e.target.value)}
              placeholder="e.g., Welcome Campaign"
              disabled={isLoading}
              autoFocus
            />
            {!requiresDescription && (
              <p className="mt-1 text-xs text-gray-500">
                This will be converted to a URL-friendly name (lowercase,
                hyphens).
              </p>
            )}
          </div>
          {requiresDescription && (
            <div>
              <label
                htmlFor="template-description"
                className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="template-description"
                value={description}
                onChange={e => onDescriptionChange(e.target.value)}
                placeholder="Describe what this automation does..."
                disabled={isLoading}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-between">
          <Button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            variant="outline">
            Back
          </Button>
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            className="bg-primary">
            {isLoading ? 'Creating...' : 'Create Template'}
          </Button>
        </div>
      </form>
    </>
  );
};
